/**
 * RV-130。管理画面の作成で結果が分からなかった X-Request-ID と本文を残す場所（このタブ）。
 *
 * EV-128（配送先）で作ったものを、作業マスタ（EV-129）・機器（EV-130）・車両（EV-131）の
 * 作成でも使えるよう、作るもの（resource）ごとの保存キーに分けた。キーが別なので、ある作成の
 * 値が別の作成の値を上書き・消去しない。
 *
 * 値の意味と使い方は config/idempotentCreate.ts（unconfirmedStore の load / save / clear）。
 * ここは保存の仕組みだけを持つ。ログアウト・失効では config/session.ts の clearSession が
 * discardAllCreateRecords を呼ぶ。
 *
 * sessionStorage に書き、同じ値をメモリにも持つ。RV-132。書けない環境（容量・制限された
 * モード）でも読むと null が返るだけの場合があるので、残っている値が無ければメモリの値を使う。
 * 書けなかったときは、古い値が残って新しいメモリの値より優先されないよう、残っている値を消す。
 */
export const CREATE_RECORD_KEYS = {
  // EV-128。以前の config/clientCreateStore.ts と同じキー（残っている値をそのまま使う）。
  client: 'clientCreateUnconfirmed',
  task: 'taskCreateUnconfirmed',
  device: 'deviceCreateUnconfirmed',
  vehicle: 'vehicleCreateUnconfirmed',
  // EV-461。運転者の登録（registerDriver）。結果が分からなかった登録を同じ X-Request-ID で送り直す。
  driver: 'driverCreateUnconfirmed',
  // YAC-2026-009 PR5b。運営の画面の作成と招待（会社の登録・管理者の招待・運営の招待）。
  operatorCompany: 'operatorCompanyCreateUnconfirmed',
  operatorManager: 'operatorManagerInviteUnconfirmed',
  operatorAccount: 'operatorInviteUnconfirmed',
} as const;

export type CreateResource = keyof typeof CREATE_RECORD_KEYS;

const memory = new Map<CreateResource, unknown>();

/** 残っている値（sessionStorage にあればそれ、無ければメモリ）。どちらも無ければ null。 */
export function readCreateRecord(resource: CreateResource): any {
  try {
    const raw = sessionStorage.getItem(CREATE_RECORD_KEYS[resource]);
    if (raw) return JSON.parse(raw);
  } catch {
    /* 読めない場合はメモリの値 */
  }
  return memory.get(resource) ?? null;
}

export function writeCreateRecord(resource: CreateResource, entry: unknown): void {
  memory.set(resource, entry);
  const key = CREATE_RECORD_KEYS[resource];
  try {
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // 書けなかった。前に書けた古い値が残っていれば、メモリの値より優先されないよう消す。
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* 保存領域が使えない場合はメモリだけ */
    }
  }
}

export function discardCreateRecord(resource: CreateResource): void {
  memory.delete(resource);
  try {
    sessionStorage.removeItem(CREATE_RECORD_KEYS[resource]);
  } catch {
    /* 保存領域が使えない場合はメモリだけ消す */
  }
}

/**
 * EV-284（PR #266 の Codex の指摘 4270014211）。招待の再送で結果が分からなかった X-Request-ID の置き場。
 * 運転者ごとのキー（resendInvitationUnconfirmed:<運転者の id>）で、このタブの sessionStorage とメモリに残す。
 * 再読み込み・画面の移動の後に押し直しても同じ値で送る（サーバーは前回の応答を返し、2通目を送らない）。
 * 値の意味と使い方は components/Driver/DriverView/ResendInvitation.tsx。
 */
const RESEND_PREFIX = 'resendInvitationUnconfirmed:';
const resendMemory = new Map<string, unknown>();

export function readResendRecord(driver: string): any {
  const key = RESEND_PREFIX + driver;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch {
    /* 読めない場合はメモリの値 */
  }
  return resendMemory.get(key) ?? null;
}

export function writeResendRecord(driver: string, entry: unknown): void {
  const key = RESEND_PREFIX + driver;
  resendMemory.set(key, entry);
  try {
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* 保存領域が使えない場合はメモリだけ */
    }
  }
}

export function discardResendRecord(driver: string): void {
  const key = RESEND_PREFIX + driver;
  resendMemory.delete(key);
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* 保存領域が使えない場合はメモリだけ消す */
  }
}

/** ログアウト・失効で、すべての作成の値を捨てる（sessionStorage とメモリの両方）。 */
export function discardAllCreateRecords(): void {
  (Object.keys(CREATE_RECORD_KEYS) as CreateResource[]).forEach(discardCreateRecord);
  // EV-284。招待の再送の値も捨てる。
  resendMemory.clear();
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(RESEND_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* 保存領域が使えない場合はメモリだけ消す */
  }
}
